import React, { useState } from 'react';
import { Cascader, Checkbox, Radio, Slider, Switch, Tooltip } from 'antd';
import { SettingOutlined } from '@ant-design/icons';

const populationOptions = [
  { label: <span className="tag-content"><span className="tag-icon">👦</span>少年群体（14岁以下）</span>, value: 'youth' },
  { label: <span className="tag-content"><span className="tag-icon">👨</span>中年群体（15-59岁）</span>, value: 'labor' },
  { label: <span className="tag-content"><span className="tag-icon">👴</span>老年群体</span>, value: 'elderly' },
];

const disabledTags = [
  ['🏡', '社区'], ['🎓', '校园'], ['🏢', '写字楼'], ['🏨', '酒店'],
  ['🌄', '景区'], ['🏥', '医院'], ['🚉', '交通枢纽'], ['🛍️', '商圈'],
];
const disabledScenes = [
  ['🌅', '早间通勤'], ['☀️', '日间办公'], ['🌆', '晚间休闲'],
  ['🌙', '夜间生活'], ['🗓️', '工作日'], ['🎉', '周末'],
];

const FilterPanel = ({
  storeTypes, categories, supplyLayerEnabled, selectedCategory,
  selectedPopulationTypes, selectedIncomeLevel,
  demandLayerEnabled, mapLayer,
  historyCount,
  onApplyFilter, onSupplyLayerToggle, onCategoryChange,
  onPopulationTypesChange,
  onIncomeLevelChange, onDemandLayerToggle, onMapLayerChange, onOpenWeights, onOpenHistory,
}) => {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedOperatingCategories, setSelectedOperatingCategories] = useState([]);
  const [o2oSource, setO2oSource] = useState('all');
  const [meituanThreshold, setMeituanThreshold] = useState(0.6);
  const [elemeThreshold, setElemeThreshold] = useState(0.6);

  const apply = (
    types = selectedTypes,
    source = o2oSource,
    meituan = meituanThreshold,
    eleme = elemeThreshold,
    operatingCategories = selectedOperatingCategories,
  ) => {
    const parentTypes = [];
    const childTypes = [];
    types.forEach(path => {
      if (path.length === 1) parentTypes.push(path[0]);
      if (path.length > 1) childTypes.push(path[path.length - 1]);
    });
    const operatingFirstCategories = [];
    const operatingSecondCategories = [];
    operatingCategories.forEach(path => {
      if (path.length > 0) operatingFirstCategories.push(path[0]);
      if (path.length > 1) operatingSecondCategories.push(path[path.length - 1]);
    });
    onApplyFilter({
      parentTypes: [...new Set(parentTypes)],
      childTypes: [...new Set(childTypes)],
      operatingFirstCategories: [...new Set(operatingFirstCategories)],
      operatingSecondCategories: [...new Set(operatingSecondCategories)],
      meituanConfidence: meituan,
      elemeConfidence: eleme,
      o2oSource: source,
    });
  };

  const handleTypes = value => {
    setSelectedTypes(value);
    apply(value, o2oSource);
  };

  const handleSource = value => {
    setO2oSource(value);
    apply(selectedTypes, value);
  };

  const handleOperatingCategories = value => {
    setSelectedOperatingCategories(value);
    apply(selectedTypes, o2oSource, meituanThreshold, elemeThreshold, value);
  };

  return (
    <aside className="compass-filter">
      <button className="history-button" onClick={onOpenHistory}>
        <span>📋</span> 历史清单 {historyCount > 0 && <b>（{historyCount}）</b>}
      </button>

      <section className="filter-section supply-section">
        <div className="section-heading">
          <span><i className="section-dot supply-dot" />目标门店 <small>必选</small></span>
          <Switch size="small" checked={supplyLayerEnabled} onChange={onSupplyLayerToggle} />
        </div>
        <label className="field-label"><span>门店类型</span><small>可多选</small></label>
        <Cascader className="full-control" options={storeTypes} multiple value={selectedTypes}
          onChange={handleTypes} placeholder="请选择门店类型" maxTagCount={2}
          showCheckedStrategy={Cascader.SHOW_CHILD} disabled={!supplyLayerEnabled} />

        <label className="field-label confidence-label"><span>美团门店置信度</span><small>阈值：{meituanThreshold.toFixed(2)}</small></label>
        <Slider min={0.6} max={1} step={0.01} value={meituanThreshold}
          marks={{ 0.6: '0.6', 0.8: '0.8', 1: '1.0' }} disabled={!supplyLayerEnabled}
          onChange={setMeituanThreshold}
          onChangeComplete={value => apply(selectedTypes, o2oSource, value, elemeThreshold)} />

        <label className="field-label confidence-label"><span>饿了么门店置信度</span><small>阈值：{elemeThreshold.toFixed(2)}</small></label>
        <Slider min={0.6} max={1} step={0.01} value={elemeThreshold}
          marks={{ 0.6: '0.6', 0.8: '0.8', 1: '1.0' }} disabled={!supplyLayerEnabled}
          onChange={setElemeThreshold}
          onChangeComplete={value => apply(selectedTypes, o2oSource, meituanThreshold, value)} />

        <DisabledGroup title="经营状态" values={['在营', '疑似关闭', '未知']} />
        <DisabledGroup title="连锁属性" values={['连锁品牌', '独立门店']} />

        <label className="field-label"><span>门店经营品类</span><small>按在售品类筛选</small></label>
        <Cascader className="full-control" options={categories} multiple
          value={selectedOperatingCategories} onChange={handleOperatingCategories}
          placeholder="选择经营品类..." maxTagCount={2}
          showCheckedStrategy={Cascader.SHOW_CHILD} disabled={!supplyLayerEnabled} />

        <label className="field-label"><span>O2O标签</span><small>门店数据来源平台</small></label>
        <Radio.Group className="pill-radio" value={o2oSource}
          onChange={e => handleSource(e.target.value)} disabled={!supplyLayerEnabled}>
          <Radio.Button value="all">全部</Radio.Button>
          <Radio.Button value="meituan">美团</Radio.Button>
          <Radio.Button value="eleme">饿了么</Radio.Button>
        </Radio.Group>
      </section>

      <section className="filter-section demand-section">
        <div className="section-heading">
          <span><i className="section-dot demand-dot" />需求指数</span>
          <div className="heading-actions">
            <button className="weight-link" onClick={onOpenWeights}><SettingOutlined /> 权重设置</button>
            <Switch size="small" checked={demandLayerEnabled} onChange={onDemandLayerToggle} />
          </div>
        </div>

        <label className="field-label"><span>地图专题图层</span><small>仅影响地图着色</small></label>
        <Radio.Group className="layer-radio" value={mapLayer} onChange={e => onMapLayerChange(e.target.value)}>
          <Tooltip title="综合品类需求、年龄与收入维度，呈现区域综合机会。"><Radio.Button value="composite">综合</Radio.Button></Tooltip>
          <Tooltip title="展示所选品类在各区域的需求强度。"><Radio.Button value="demand">需求</Radio.Button></Tooltip>
          <Tooltip title="展示所选年龄人群的区域分布强度。"><Radio.Button value="population">人口</Radio.Button></Tooltip>
          <Tooltip title="展示所选收入层级人群的区域分布。"><Radio.Button value="income">收入</Radio.Button></Tooltip>
        </Radio.Group>

        <div className="sub-filter-row"><label>品类需求</label><small>选择品类并设置权重</small></div>
        <Cascader className="full-control" options={categories} multiple value={selectedCategory}
          onChange={onCategoryChange} placeholder="选择需求品类..." maxTagCount={2}
          showCheckedStrategy={Cascader.SHOW_CHILD} />

        <div className="sub-filter-row"><label>年龄分层</label><small>按年龄段筛选+权重</small></div>
        <Checkbox.Group className="compact-checks" options={populationOptions}
          value={selectedPopulationTypes} onChange={onPopulationTypesChange} />

        <div className="sub-filter-row"><label>收入分布</label><small>按收入水平筛选+权重</small></div>
        <Radio.Group className="pill-radio income-radio" value={selectedIncomeLevel}
          onChange={e => onIncomeLevelChange(e.target.value)}>
          <Radio.Button value="high"><span className="tag-content"><span className="tag-icon">💰</span>高收入人群</span></Radio.Button>
          <Radio.Button value="middle"><span className="tag-content"><span className="tag-icon">💵</span>中收入人群</span></Radio.Button>
          <Radio.Button value="low"><span className="tag-content"><span className="tag-icon">💸</span>低收入人群</span></Radio.Button>
        </Radio.Group>

        <DisabledTagCloud title="地标标签" subtitle="叠加POI场景" tags={disabledTags} />
        <DisabledTagCloud title="场景标签" subtitle="需求场景画像" tags={disabledScenes} />
      </section>
    </aside>
  );
};

const DisabledGroup = ({ title, values }) => (
  <div className="disabled-group">
    <label className="field-label muted"><span>{title}</span></label>
    <div className="disabled-pills">{values.map(value => <span key={value}>{value}</span>)}</div>
  </div>
);

const DisabledTagCloud = ({ title, subtitle, tags }) => (
  <div className="disabled-tag-cloud">
    <label className="field-label"><span>{title}</span><small>{subtitle}</small></label>
    <div>{tags.map(([icon, label]) => (
      <span className="tag-content" key={label}><span className="tag-icon">{icon}</span>{label}</span>
    ))}</div>
  </div>
);

export default FilterPanel;
